import { GridColDef } from '@mui/x-data-grid-premium';
import React from 'react';
import { tableSettingsProps, useTableSettings } from 'src/core/tables/useTableSettings';
import tableHelper from 'src/core/tables/tableHelper';
import { useGetAllInvoices } from 'src/pages/orders/orderRequests';
import PrintInvoiceButton from 'src/shared/printing/PrintInvoiceButton';

export const useInvoicesTable = (): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const { rows, status } = useGetAllInvoices();
  const idPath = ['Id'];
  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: 'actions', getActions: (params) => tableHelper.buttonColumn(params, 'Edit', '/orders/payments/invoices/edit?id=', 'Id', 'Edit Invoice'), type: 'actions', headerName: 'View', width: 80, },
      { field: 'InvoiceNumber', headerName: 'Invoice Number', minWidth: 140 },
      { field: 'print', headerName: 'Print', sortable: false, filterable: false, minWidth: 120, maxWidth: 140, align: 'center', headerAlign: 'center', renderCell: (params) => <PrintInvoiceButton iconButton={true} invoiceId={params.row?.Id} /> },
      { field: 'CustomerAccountId', headerName: 'Customer Account Id', type: 'number', minWidth: 160 },
      { field: 'CustomerAccountName', headerName: 'Customer Name', flex: 1, minWidth: 240 },
      { field: 'Status_Value', headerName: 'Status', valueGetter: tableHelper.getFieldKeyOrValue, renderCell: tableHelper.renderStatusCell, minWidth: 180 },
      { field: 'SalesType_Value', headerName: 'Sales Type', valueGetter: tableHelper.getFieldKeyOrValue, minWidth: 220 },
      { field: 'Currency_Value', headerName: 'Currency', valueGetter: tableHelper.getFieldKeyOrValue, minWidth: 120 },
      { field: 'CurrencyExchangeRate', headerName: 'Exchange Rate', type: 'number', minWidth: 140 },
      { field: 'TotalAmountGross', headerName: 'Total Amount Gross', type: 'number', minWidth: 180 },
      { field: 'TotalAmountNet', headerName: 'Total Amount Net', type: 'number', minWidth: 170 },
      { field: 'TotalTaxAmount', headerName: 'Total Tax Amount', type: 'number', minWidth: 160 },
      { field: 'PennyDifference', headerName: 'Penny Difference', type: 'number', minWidth: 160 },
      { field: 'ToPayAmount', headerName: 'To Pay Amount', type: 'number', minWidth: 160 },
      { field: 'VatRegistrationNumber', headerName: 'VAT Registration Number', minWidth: 220 },
      { field: 'TaxIncluded', headerName: 'Tax Included', type: 'boolean', minWidth: 130 },
      { field: 'Overdue', headerName: 'Overdue', type: 'boolean', minWidth: 110 },
      { field: 'CreateDate', headerName: 'Created At', type: 'dateTime', valueGetter: tableHelper.getDateColumn, minWidth: 180 },
      { field: 'InvoiceDate', headerName: 'Invoice Date', type: 'dateTime', valueGetter: tableHelper.getDateColumn, minWidth: 170 },
      { field: 'PaymentDue', headerName: 'Payment Due', type: 'dateTime', valueGetter: tableHelper.getDateColumn, minWidth: 170 },
      { field: 'CreatedBy', headerName: 'Created By', minWidth: 160 },
      { field: 'NrOfPayments', headerName: 'Number of Payments', type: 'number', minWidth: 180 },
      { field: 'SalesOrganizationId', headerName: 'Sales Organization Id', type: 'number', minWidth: 190 },
      { field: 'Id', headerName: 'Invoice Id' },
      { field: 'LegalText', headerName: 'Legal Text', flex: 1, minWidth: 200 }
    ],
    []
  );
  const tableName = 'InvoicesTable';
  const tableSettings = useTableSettings({ tableName, idPath, columns, rows, status, defaultColumnVisibilityModel: { LegalText: false } });
  return [columns, tableSettings];
};

export const useInvoiceRowsTable = (initialValues: any): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const rows = initialValues?.InvoiceRows ?? [];
  const idPath = ['Id'];
  const tableName = 'InvoiceRowsTable';
  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: 'RowNumber', headerName: 'Row #', type: 'number', width: 90 },
      { field: 'SalesItemArticleNumber', headerName: 'Article #', width: 160 },
      { field: 'Specification', headerName: 'Specification', flex: 1, minWidth: 240 },
      { field: 'Quantity', headerName: 'Qty', type: 'number', width: 110 },
      { field: 'PriceNoG', headerName: 'Price (NoG)', type: 'number', width: 150 },
      { field: 'DiscountRate', headerName: 'Discount %', type: 'number', width: 130 },
      { field: 'DiscountAmountNoG', headerName: 'Discount (NoG)', type: 'number', width: 160 },
      { field: 'ListPriceNoG', headerName: 'List Price (NoG)', type: 'number', width: 170 },
      { field: 'TaxRate', headerName: 'Tax %', type: 'number', width: 110 },
      { field: 'TaxAmount', headerName: 'Tax Amount', type: 'number', width: 140 },
      { field: 'RowTotalAmountNoG', headerName: 'Row Total (NoG)', type: 'number', width: 170 },
      { field: 'RowTotalAmountNet', headerName: 'Row Total (Net)', type: 'number', width: 170 },
      { field: 'Comment', headerName: 'Comment', flex: 1, minWidth: 180 }
    ],
    []
  );
  const tableSettings = useTableSettings({ tableName, columns, rows, idPath });
  tableSettings.autoHeight = true;
  tableSettings.loading = false;
  tableSettings.hasToolbar = false;
  return [columns, tableSettings];
};

export const useInvoiceRowDetailsTable = (rows: any[] = []): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const idPath = ['Id'];
  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: 'Id', headerName: 'Id', width: 90 },
      { field: 'DetailId', headerName: 'Detail Id', width: 160 },
      { field: 'Specification', headerName: 'Specification', flex: 1, minWidth: 240 },
      { field: 'Quantity', headerName: 'Qty', type: 'number', width: 110 },
      { field: 'Comment', headerName: 'Comment', flex: 1, minWidth: 200 }
    ],
    []
  );
  const tableName = 'InvoiceRowDetailsTable';
  const tableSettings = useTableSettings({ tableName, idPath, columns, rows });
  tableSettings.hasToolbar = false;
  tableSettings.autoHeight = true;
  tableSettings.loading = false;
  tableSettings.hideFooter = true;
  return [columns, tableSettings];
};
